import CompanyLayout from '@/components/feature/CompanyLayout';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const productNavItems = [
  { path: '/markets', label: 'Markets' },
  { path: '/ai-agents', label: 'AI Agents' },
  { path: '/vaults', label: 'Vaults' },
  { path: '/leaderboard', label: 'Leaderboard' },
  { path: '/fees', label: 'Fees' },
];

export default function FeesPage() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const bgCard = isDark ? 'bg-[#0a0a0a]' : 'bg-white';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#888]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#555]' : 'text-[#999]';

  return (
    <CompanyLayout
      title={t('fees.title', 'Fees')}
      subtitle={t('fees.subtitle', 'Transparent, competitive fee structure for all traders')}
      category="Product"
      navItems={productNavItems}
    >
      {/* Perpetual Trading Fees */}
      <div className={`p-6 rounded-xl border ${borderLight} ${bgCard} mb-6`}>
        <h2 className={`text-lg font-bold mb-4 ${textPrimary}`}>{t('fees.perpetualTrading', 'Perpetual Trading')}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`border-b ${borderLight} ${textMuted}`}>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('fees.tier', 'Tier')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('fees.volume30d', '30D Volume')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('fees.maker', 'Maker Fee')}</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('fees.taker', 'Taker Fee')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className={`border-b ${borderLight}`}>
                <td className={`px-4 py-3 font-medium ${textPrimary}`}>VIP 0</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>&lt; $100K</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>0.02%</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>0.05%</td>
              </tr>
              <tr className={`border-b ${borderLight}`}>
                <td className={`px-4 py-3 font-medium ${textPrimary}`}>VIP 1</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>$100K - $1M</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>0.015%</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>0.04%</td>
              </tr>
              <tr className={`border-b ${borderLight}`}>
                <td className={`px-4 py-3 font-medium ${textPrimary}`}>VIP 2</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>$1M - $10M</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>0.01%</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>0.035%</td>
              </tr>
              <tr className={`border-b ${borderLight}`}>
                <td className={`px-4 py-3 font-medium ${textPrimary}`}>VIP 3</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>$10M - $50M</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>0.005%</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>0.03%</td>
              </tr>
              <tr>
                <td className={`px-4 py-3 font-medium ${textPrimary}`}>VIP 4</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>&gt; $50M</td>
                <td className="px-4 py-3 text-right font-mono text-[#00ff88]">0.00%</td>
                <td className={`px-4 py-3 text-right font-mono ${textSecondary}`}>0.025%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Funding Rates */}
      <div className={`p-6 rounded-xl border ${borderLight} ${bgCard} mb-6`}>
        <h2 className={`text-lg font-bold mb-4 ${textPrimary}`}>{t('fees.fundingRate', 'Funding Rates')}</h2>
        <p className={`text-sm mb-4 leading-relaxed ${textSecondary}`}>
          Funding rates are exchanged between long and short positions every 8 hours. The rate is calculated based on the premium between the perpetual contract price and the spot index price. {t('fees.fundingLong', 'Positive funding means longs pay shorts')} {t('fees.fundingShort', 'negative funding means shorts pay longs')}.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className={`p-4 rounded-lg border ${borderLight} text-center`}>
            <div className={`text-xs uppercase tracking-wider mb-1 ${textMuted}`}>{t('fees.interval', 'Interval')}</div>
            <div className={`text-lg font-bold ${textPrimary}`}>Every 8 hours</div>
          </div>
          <div className={`p-4 rounded-lg border ${borderLight} text-center`}>
            <div className={`text-xs uppercase tracking-wider mb-1 ${textMuted}`}>{t('fees.settlement', 'Settlement')}</div>
            <div className={`text-lg font-bold ${textPrimary}`}>00:00 / 08:00 / 16:00 UTC</div>
          </div>
          <div className={`p-4 rounded-lg border ${borderLight} text-center`}>
            <div className={`text-xs uppercase tracking-wider mb-1 ${textMuted}`}>{t('fees.maxRate', 'Max Rate')}</div>
            <div className={`text-lg font-bold ${textPrimary}`}>&plusmn; 0.75% / 8h</div>
          </div>
        </div>
      </div>

      {/* Withdrawal Fees */}
      <div className={`p-6 rounded-xl border ${borderLight} ${bgCard}`}>
        <h2 className={`text-lg font-bold mb-4 ${textPrimary}`}>{t('fees.withdrawalFees', 'Withdrawal Fees')}</h2>
        <p className={`text-sm mb-4 leading-relaxed ${textSecondary}`}>
          Withdrawal fees cover on-chain gas costs and are dynamically adjusted based on network congestion. There are no deposit fees.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { asset: 'BTC', fee: '0.0003 BTC' },
            { asset: 'ETH', fee: '0.002 ETH' },
            { asset: 'USDC', fee: '1.5 USDC' },
            { asset: 'USDT', fee: '1.5 USDT' },
          ].map((item) => (
            <div key={item.asset} className={`p-4 rounded-lg border ${borderLight} flex items-center justify-between`}>
              <span className={`font-medium ${textPrimary}`}>{item.asset}</span>
              <span className={`font-mono text-sm ${textSecondary}`}>{item.fee}</span>
            </div>
          ))}
        </div>
      </div>
    </CompanyLayout>
  );
}